'use client';

import {useCourseRetirementByIdQuery} from '@/generated/graphql';
import {useUserHasOrganization} from '@/lib/hooks/use-user-has-organization';

function toDateOnlyString(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
        date.getDate(),
    ).padStart(2, '0')}`;
}

/**
 * `organizationInfo.retiresOn` is the raw scheduled_removal_date from Subscription Management,
 * with none of the self-healing the monolith applies once a date is overdue (it just keeps
 * returning the same past date indefinitely if the content team is late to actually remove the
 * course). Treat an overdue date as "no retirement to show" rather than surfacing a stale one.
 */
function isOverdue(retiresOn: string): boolean {
    return retiresOn.slice(0, 10) < toDateOnlyString(new Date());
}

/**
 * Fetches the org-scoped course retirement date, client-side only.
 *
 * `organizationInfo.retiresOn` resolves from the viewer's JWT org context, so it must never be
 * fetched as part of the ISR'd page shell (it would bake one org's banner into the shared cache).
 */
export function useCourseRetirement(courseId: string) {
    const hasOrganization = useUserHasOrganization();

    const courseRetirement = useCourseRetirementByIdQuery(
        {id: courseId},
        {enabled: hasOrganization},
    );

    if (!hasOrganization || courseRetirement.isError) {
        return {isLoading: false, retiresOn: undefined};
    }
    const rawRetiresOn =
        (courseRetirement.data?.course?.organizationInfo?.retiresOn as string | undefined) ??
        undefined;
    const retiresOn = rawRetiresOn && !isOverdue(rawRetiresOn) ? rawRetiresOn : undefined;

    return {
        isLoading: courseRetirement.isLoading,
        retiresOn,
    };
}
